import type { SupabaseClient } from '@supabase/supabase-js';
import type { DictEntry } from './dictionary';
import { normalizePinyinText, normalizePinyinWith } from './pinyin';

// The two things shared domain code needs from its host, injected once at startup because each
// surface builds them differently (ADR 0036): the mobile app's Supabase client persists sessions
// through AsyncStorage and its dictionary comes from a bundled asset, while the Admin Console will
// build a browser client and fetch the dictionary into IndexedDB.

export interface DictionaryProvider {
  getDictionary(): Promise<Map<string, DictEntry>>;
  getMultiDictionary(): Promise<Map<string, DictEntry[]>>;
}

let client: SupabaseClient | null = null;
let dictionary: DictionaryProvider | null = null;

// `dictionary` is optional: the Admin Console has no dictionary until the phrase grid needs one,
// and the two functions that use it degrade honestly without it — getDictionary rejects with a
// named error, and normalizePinyin falls back to text normalisation.
export function setSharedRuntime(config: {
  client: SupabaseClient;
  dictionary?: DictionaryProvider;
}): void {
  client = config.client;
  dictionary = config.dictionary ?? null;
}

// A forwarding handle rather than the client itself, so shared modules can `import { supabase }`
// at module load while the real client is created later by the host. Reaching for it before
// setSharedRuntime() has run is a wiring bug, and says so rather than failing as `undefined is not
// a function` several frames deeper.
export const supabase: SupabaseClient = new Proxy({} as SupabaseClient, {
  get(_target, property, receiver) {
    if (!client) {
      throw new Error(
        'Shared Supabase client not configured — call setSharedRuntime() during startup.',
      );
    }
    return Reflect.get(client as object, property, receiver);
  },
});

export function getDictionary(): Promise<Map<string, DictEntry>> {
  if (!dictionary) {
    return Promise.reject(new Error('Dictionary provider not configured — call setSharedRuntime().'));
  }
  return dictionary.getDictionary();
}

export function getMultiDictionary(): Promise<Map<string, DictEntry[]>> {
  if (!dictionary) {
    return Promise.reject(new Error('Dictionary provider not configured — call setSharedRuntime().'));
  }
  return dictionary.getMultiDictionary();
}

// CC-CEDICT is the primary source (it guarantees correct syllable spacing); text normalisation is
// the fallback when the phrase is unknown or the dictionary fails to load. A missing dictionary is
// not fatal — the text-normalised result still beats the raw input.
export async function normalizePinyin(pinyin: string, chinese: string): Promise<string> {
  try {
    if (!dictionary) return normalizePinyinText(pinyin);
    return normalizePinyinWith(await dictionary.getMultiDictionary(), pinyin, chinese);
  } catch {
    return normalizePinyinText(pinyin);
  }
}
